// src/features/dashboard/components/EmployeeIdleTime/IdleMinutesChart.tsx

import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  Cell,
  Label,
} from 'recharts';

import { Lightbulb } from 'lucide-react';

import { IdleMinuteByHourResponse } from '../../types/Employee/employeeIdleTime.types';

interface Props {
  data: IdleMinuteByHourResponse[];
  target: number;
  loading?: boolean;
}

const BAR_COLOR = '#FF812D';
const HOVER_COLOR = '#FF6B1A';
const GRID = '#ECECEC';
const THRESHOLD = '#F87171';

/* ========================================================================== */
/* TIME HELPERS                                                               */
/* ========================================================================== */

const parseTime = (
  value: string | number,
) => {
  const raw = String(
    value ?? '',
  ).trim();

  if (!raw) {
    return {
      hour: 0,
      minute: 0,
    };
  }

  /*
   * Supports:
   *
   * 09:00
   * 09:30
   * 9:00
   * 9:30
   */
  if (raw.includes(':')) {
    const [
      hourPart,
      minutePart,
    ] = raw.split(':');

    const hour = Number(
      hourPart,
    );

    const minute = Number(
      minutePart,
    );

    return {
      hour: Number.isFinite(hour)
        ? hour
        : 0,

      minute: Number.isFinite(
        minute,
      )
        ? minute
        : 0,
    };
  }

  /*
   * Supports:
   *
   * 9
   * 9.5
   *
   * 9.5 = 09:30
   */
  const numericValue =
    Number(raw);

  if (
    !Number.isFinite(
      numericValue,
    )
  ) {
    return {
      hour: 0,
      minute: 0,
    };
  }

  const hour = Math.floor(
    numericValue,
  );

  const minute = Math.round(
    (numericValue - hour) * 60,
  );

  return {
    hour,
    minute,
  };
};

/* ========================================================================== */
/* AXIS TIME FORMAT                                                           */
/* ========================================================================== */

const formatAxisTime = (
  hour: number,
  minute: number,
) => {
  const formattedHour =
    String(hour).padStart(
      2,
      '0',
    );

  if (minute === 0) {
    return formattedHour;
  }

  return `${formattedHour}:${String(
    minute,
  ).padStart(2, '0')}`;
};

/* ========================================================================== */
/* TOOLTIP                                                                    */
/* ========================================================================== */

const CustomTooltip = ({
  active,
  payload,
}: any) => {
  if (
    !active ||
    !payload?.length
  ) {
    return null;
  }

  const row =
    payload[0]?.payload;

  if (!row) {
    return null;
  }

  const idleMinutes = Number(
    row.idleMinutes ?? 0,
  );

  if (idleMinutes <= 0) {
    return null;
  }

  return (
    <div
      className="
        pointer-events-none
        min-w-[130px]
        overflow-hidden
        rounded-[6px]
        border
        border-[#E4E7EC]
        bg-white
        shadow-lg
      "
    >
      <div
        className="
          border-b
          border-[#EAECF0]
          bg-[#F8F9FB]
          px-3
          py-2
          text-[11px]
          font-medium
          text-[#344054]
        "
      >
        {row.tooltipTime}
      </div>

      <div
        className="
          flex
          items-center
          gap-2
          px-3
          py-2
        "
      >
        <span
          className="
            h-3
            w-3
            shrink-0
            rounded-full
          "
          style={{
            backgroundColor:
              BAR_COLOR,
          }}
        />

        <span className="text-[12px] text-[#344054]">
          Idle mins:
        </span>

        <span
          className="
            ml-auto
            text-[12px]
            font-semibold
            text-[#101828]
          "
        >
          {idleMinutes.toFixed(0)}m
        </span>
      </div>
    </div>
  );
};

/* ========================================================================== */
/* THRESHOLD LABEL                                                            */
/* ========================================================================== */

const SpikeLabel = (
  props: any,
) => {
  const {
    viewBox,
    value,
  } = props;

  if (!viewBox) {
    return null;
  }

  const labelWidth = 82;

  const x =
    viewBox.x +
    viewBox.width -
    labelWidth;

  const y =
    viewBox.y - 10;

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={labelWidth}
        height={18}
        rx={3}
        fill="#FFF5F5"
        stroke="#FCA5A5"
      />

      <text
        x={
          x +
          labelWidth / 2
        }
        y={
          viewBox.y + 3
        }
        textAnchor="middle"
        fontSize="10"
        fill="#EF4444"
      >
        {value}m threshold
      </text>
    </g>
  );
};

/* ========================================================================== */
/* COMPONENT                                                                  */
/* ========================================================================== */

const IdleMinutesChart = ({
  data,
  target,
  loading = false,
}: Props) => {
  const [
    activeIndex,
    setActiveIndex,
  ] = useState<number | null>(
    null,
  );

  /*
   * Used only to determine how many
   * X-axis labels should be visible.
   *
   * Bars are NEVER removed.
   */
  const chartWrapperRef =
    useRef<HTMLDivElement | null>(
      null,
    );

  const [
    chartWidth,
    setChartWidth,
  ] = useState(0);

  /* ======================================================================== */
  /* RESPONSIVE WIDTH                                                         */
  /* ======================================================================== */

  useEffect(() => {
    const element =
      chartWrapperRef.current;

    if (!element) {
      return;
    }

    const updateWidth = () => {
      setChartWidth(
        element.clientWidth,
      );
    };

    updateWidth();

    const observer =
      new ResizeObserver(
        updateWidth,
      );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, []);

  /* ======================================================================== */
  /* CHART DATA                                                               */
  /* ======================================================================== */

  const chartData = useMemo(() => {
    if (!Array.isArray(data)) {
      return [];
    }

    return data.map(
      (item, index) => {
        const {
          hour,
          minute,
        } = parseTime(
          item.hour,
        );

        return {
          index,

          hour,

          minute,

          /*
           * X-axis display:
           *
           * 09
           * 09:30
           * 10
           * 10:30
           */
          time: formatAxisTime(
            hour,
            minute,
          ),

          /*
           * Tooltip display:
           *
           * 09:00
           * 09:30
           */
          tooltipTime: `${String(
            hour,
          ).padStart(
            2,
            '0',
          )}:${String(
            minute,
          ).padStart(
            2,
            '0',
          )}`,

          idleMinutes:
            Number(
              item.idleMinutes ?? 0,
            ),
        };
      },
    );
  }, [data]);

  /* ======================================================================== */
  /* DYNAMIC Y AXIS                                                           */
  /* ======================================================================== */

  const maxValue = useMemo(() => {
    return Math.max(
      target,
      ...chartData.map(
        (item) =>
          item.idleMinutes,
      ),
      0,
    );
  }, [
    target,
    chartData,
  ]);

  const yAxisMax =
    maxValue === 0
      ? 10
      : Math.ceil(
          (maxValue + 10) / 10,
        ) * 10;

  /* ======================================================================== */
  /* MAX IDLE                                                                 */
  /* ======================================================================== */

  const maxIdle = useMemo(() => {
    return Math.max(
      ...chartData.map(
        (item) =>
          item.idleMinutes,
      ),
      0,
    );
  }, [chartData]);

  /* ======================================================================== */
  /* THRESHOLD                                                                */
  /* ======================================================================== */

  const spikeThreshold =
    maxIdle > 0
      ? Math.ceil(
          maxIdle * 0.8,
        )
      : 0;

  /* ======================================================================== */
  /* RESPONSIVE X AXIS INTERVAL                                               */
  /* ======================================================================== */

  /*
   * IMPORTANT:
   *
   * This controls ONLY the visibility of
   * labels.
   *
   * It does NOT remove bars.
   *
   * Example:
   *
   * Desktop:
   *
   * 09 09:30 10 10:30 11 11:30 12 ...
   *
   * Small screen:
   *
   * 09       10       11       12 ...
   */
  const xAxisInterval = useMemo(() => {
    const totalPoints =
      chartData.length;

    if (totalPoints <= 1) {
      return 0;
    }

    /*
     * If width has not been measured yet,
     * show every point.
     */
    if (chartWidth <= 0) {
      return 0;
    }

    /*
     * Approximate width required
     * for one readable label.
     */
    const labelWidth =
      chartWidth < 400
        ? 38
        : chartWidth < 500
          ? 42
          : chartWidth < 650
            ? 48
            : 55;

    /*
     * Calculate how many labels
     * can reasonably fit.
     */
    const availableLabels =
      Math.max(
        2,
        Math.floor(
          chartWidth /
            labelWidth,
        ),
      );

    if (
      totalPoints <=
      availableLabels
    ) {
      return 0;
    }

    /*
     * Recharts interval is zero based.
     *
     * interval = 1
     * means every second label.
     *
     * interval = 2
     * means every third label.
     */
    return Math.max(
      1,
      Math.ceil(
        totalPoints /
          availableLabels,
      ) - 1,
    );
  }, [
    chartData.length,
    chartWidth,
  ]);

  /* ======================================================================== */
  /* LOADING                                                                  */
  /* ======================================================================== */

  if (loading) {
    return (
      <div
        className="
          h-[360px]
          w-full
          animate-pulse
          rounded-[18px]
          bg-gray-200
        "
      />
    );
  }

  /* ======================================================================== */
  /* EMPTY DATA                                                               */
  /* ======================================================================== */

  if (!chartData.length) {
    return (
      <div
        className="
          flex
          h-[360px]
          w-full
          items-center
          justify-center
          rounded-[18px]
          border
          border-[#D0D5DD]
          bg-white
          text-sm
          text-gray-500
        "
      >
        No idle time data available.
      </div>
    );
  }

  /* ======================================================================== */
  /* RENDER                                                                   */
  /* ======================================================================== */

  return (
    <div
      className="
        w-full
        min-w-0
        overflow-hidden
        rounded-[18px]
        border
        border-[#D0D5DD]
        bg-white
        px-4
        pt-5
        pb-4

        sm:px-5
        lg:px-6
      "
    >
      {/* ================================================================== */}
      {/* HEADER                                                              */}
      {/* ================================================================== */}

      <div
        className="
          mb-5
          flex
          min-w-0
          flex-col
          gap-3

          sm:flex-row
          sm:items-center
          sm:justify-between
        "
      >
        <h3
          className="
            min-w-0
            text-[15px]
            font-semibold
            text-[#002B5B]
          "
        >
          Idle Minutes by Hour · Today
        </h3>

        <div
          className="
            w-fit
            max-w-full
            rounded-full
            bg-[#EEF4FF]
            px-4
            py-[5px]
            text-[11px]
            leading-4
            text-[#2563EB]

            sm:text-[12px]
          "
        >
          When idle time occurred and for how long
        </div>
      </div>

      {/* ================================================================== */}
      {/* CHART                                                              */}
      {/* ================================================================== */}

      <div
        ref={chartWrapperRef}
        className="
          w-full
          min-w-0
          overflow-hidden
        "
      >
        <div
          className="
            h-[235px]
            w-full
          "
        >
          <ResponsiveContainer
            width="100%"
            height="100%"
          >
            <BarChart
              data={chartData}
              margin={{
                top: 12,
                right:
                  chartWidth < 500
                    ? 40
                    : 55,
                left:
                  chartWidth < 500
                    ? 0
                    : 5,
                bottom: 38,
              }}
              barCategoryGap={
                chartWidth < 500
                  ? '12%'
                  : '18%'
              }
            >
              {/* ======================================================== */}
              {/* GRID                                                       */}
              {/* ======================================================== */}

              <CartesianGrid
                vertical={false}
                stroke={GRID}
                strokeDasharray="0"
              />

              {/* ======================================================== */}
              {/* X AXIS                                                     */}
              {/* ======================================================== */}

              <XAxis
                dataKey="time"
                type="category"

                /*
                 * Dynamic label interval.
                 *
                 * Bars remain ALL visible.
                 */
                interval={
                  xAxisInterval
                }

                minTickGap={4}

                axisLine={{
                  stroke:
                    '#D0D5DD',
                }}

                tickLine={false}

                tick={{
                  fontSize:
                    chartWidth < 400
                      ? 8
                      : 9,
                  fill: '#344054',
                }}

                tickMargin={6}

                /*
                 * Matches your screenshot.
                 */
                angle={-35}

                textAnchor="end"

                height={55}
              />

              {/* ======================================================== */}
              {/* Y AXIS                                                     */}
              {/* ======================================================== */}

              <YAxis
                width={
                  chartWidth < 400
                    ? 36
                    : 42
                }
                domain={[
                  0,
                  yAxisMax,
                ]}
                tickCount={6}
                tickFormatter={(
                  value,
                ) =>
                  `${value}m`
                }
                axisLine={false}
                tickLine={false}
                tick={{
                  fontSize: 10,
                  fill: '#344054',
                }}
              />

              {/* ======================================================== */}
              {/* TOOLTIP                                                    */}
              {/* ======================================================== */}

              <Tooltip
                cursor={{
                  fill: 'rgba(255, 129, 45, 0.04)',
                }}
                content={
                  <CustomTooltip />
                }
                wrapperStyle={{
                  zIndex: 9999,
                  outline: 'none',
                }}
                allowEscapeViewBox={{
                  x: false,
                  y: true,
                }}
              />

              {/* ======================================================== */}
              {/* THRESHOLD                                                  */}
              {/* ======================================================== */}

              {spikeThreshold > 0 && (
                <ReferenceLine
                  y={
                    spikeThreshold
                  }
                  stroke={
                    THRESHOLD
                  }
                  strokeWidth={1.2}
                  strokeDasharray="5 5"
                >
                  <Label
                    position="right"
                    value={
                      spikeThreshold
                    }
                    content={
                      <SpikeLabel />
                    }
                  />
                </ReferenceLine>
              )}

              {/* ======================================================== */}
              {/* BARS                                                       */}
              {/* ======================================================== */}

              <Bar
                dataKey="idleMinutes"
                name="Idle mins"
                radius={[
                  4,
                  4,
                  0,
                  0,
                ]}
                barSize={
                  chartWidth < 400
                    ? 10
                    : chartWidth < 600
                      ? 13
                      : 18
                }
                isAnimationActive={
                  false
                }
                onMouseEnter={(
                  _,
                  index,
                ) => {
                  setActiveIndex(
                    index,
                  );
                }}
                onMouseLeave={() => {
                  setActiveIndex(
                    null,
                  );
                }}
              >
                {chartData.map(
                  (
                    item,
                    index,
                  ) => {
                    const isActive =
                      activeIndex ===
                      index;

                    return (
                      <Cell
                        key={`${item.time}-${index}`}
                        fill={
                          isActive
                            ? HOVER_COLOR
                            : BAR_COLOR
                        }
                        fillOpacity={
                          activeIndex ===
                            null ||
                          isActive
                            ? 1
                            : 0.85
                        }
                      />
                    );
                  },
                )}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ================================================================== */}
      {/* INSIGHT                                                             */}
      {/* ================================================================== */}

      {/* <div
        className="
          mt-5
          flex
          items-start
          gap-2
          rounded-[8px]
          border
          border-[#F6C344]
          bg-[#FFF9E8]
          px-3
          py-3
        "
      >
        <Lightbulb
          size={14}
          className="
            mt-[1px]
            shrink-0
            text-[#F59E0B]
          "
          fill="#FACC15"
        />

        <p
          className="
            text-[11px]
            leading-[18px]
            text-[#D97706]
          "
        >
          <span className="font-semibold">
            Insight:
          </span>{' '}
          Your 30-min post-lunch idle
          (13:30–14:00) is a recurring
          pattern this week. Try a
          5-minute planning session
          immediately after lunch to stay
          on track.
        </p>
      </div> */}
    </div>
  );
};

export default IdleMinutesChart;